import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useModel } from 'umi';
import { Popover, UserFeedback } from '@bluevision/components';
import type { Timeout } from 'ahooks/lib/useRequest/src/types';

import { APP_NAME, LocalStorageKey, StringBoolean } from '@/data/constant';
import { isProdDomain } from '@/utils';
import { FEEDBACK_TIPS_CLOSE_TIME } from './constant';

const Feedback = () => {
  const { t } = useTranslation();
  const { initialState } = useModel('@@initialState');

  /** 用户引导 popover 开启状态 */
  const [tipsOpen, setTipsOpen] = useState(false);
  /** 提醒关闭的倒计时变量（组件卸载时销毁） */
  const tipsCloseTimeout = useRef<Timeout | undefined>();

  /** 获取 提示 是否弹出过 */
  const getPopoverLocalValue = useCallback(() => {
    return localStorage.getItem(LocalStorageKey.FEEDBACK_POPOVER) || StringBoolean.FALSE;
  }, []);

  /** 设置 提示 已弹出 */
  const setPopoverEjected = useCallback(() => {
    localStorage.setItem(LocalStorageKey.FEEDBACK_POPOVER, StringBoolean.TRUE);
  }, []);

  /** 弹出提示 3s 并关闭 */
  const startPopUp = useCallback(() => {
    setTipsOpen(true);
    tipsCloseTimeout.current = setTimeout(() => {
      setTipsOpen(false);
    }, FEEDBACK_TIPS_CLOSE_TIME);
  }, []);

  /** 弹出 popover 且倒计时关闭（如果没弹过） */
  useEffect(() => {
    const notPopover = getPopoverLocalValue() === StringBoolean.FALSE;
    if (notPopover) {
      startPopUp();
      setPopoverEjected();
    }

    return () => {
      if (tipsCloseTimeout.current) {
        clearTimeout(tipsCloseTimeout.current);
      }
    };
  }, [getPopoverLocalValue, setPopoverEjected, startPopUp]);

  /** 当前运行环境 */
  const runtimeEnv = useMemo(() => {
    // 类型在此，但由于目录不稳定，所以未引入，传入 prod / dev 即可（node_modules/@bluevision/components/types/common/environment/constant.d.ts）
    return isProdDomain() ? 'prod' : 'dev';
  }, []);

  return (
    <Popover open={tipsOpen} content={t('new_feature_click_here_to_give_feedback')}>
      <UserFeedback
        app={APP_NAME}
        knobTheme='black'
        env={runtimeEnv}
        uid={initialState?.currentUser?.username}
      />
    </Popover>
  );
};

export default Feedback;
